import { PropsWithClassName } from 'lib-shared-types-utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { appActions } from 'app/app.actions';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { pubSub } from 'utils/pubSub';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { MimSidekickLauncher } from './components/mimSidekickLauncher/mimSidekickLauncher';
import {
  MimSidekickWidget,
  type PendingPrompt,
} from './components/mimSidekickWidget/mimSidekickWidget';

export const MimSidekick = ({
  className,
}: PropsWithClassName): JSX.Element | null => {
  const { mimSidekick } = useFeatureFlag(['mim-sidekick']);
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState<PendingPrompt | null>(
    null,
  );

  const isAskingRef = useRef(false);

  const start = useCallback((text: string) => {
    setPendingPrompt({ id: uuid(), text });
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // A prompt that never made it out is not owed to the next session.
    setPendingPrompt(null);
  }, []);

  useEffect(() => {
    const unsubscribe = pubSub.subscribe(
      'toggleMimSidekick',
      ({ isOpen: next }) => setIsOpen((prev) => next ?? !prev),
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubscribe = pubSub.subscribe('askMimSidekick', (request) => {
      // Nothing can come of asking while the Sidekick cannot be opened.
      if (!mimSidekick) return;

      if ('prompt' in request) {
        start(request.prompt);
        return;
      }

      // A second modal of the same type never reaches the stack, and would
      // leave this promise unsettled.
      if (isAskingRef.current) return;
      isAskingRef.current = true;

      void (async () => {
        const result = await dispatch(
          appActions.showModal({
            modal: {
              type: 'app:sidekickAsk',
              arg: {
                title: request.title,
                label: request.label,
                placeholder: request.placeholder,
              },
            },
          }),
        );

        isAskingRef.current = false;

        if (result.isCancelled) return;
        start(request.createPrompt(result.answer));
      })();
    });
    return unsubscribe;
  }, [dispatch, mimSidekick, start]);

  if (!mimSidekick) return null;
  if (!isOpen) return <MimSidekickLauncher onClick={() => setIsOpen(true)} />;
  return (
    <MimSidekickWidget
      className={className ?? ''}
      pendingPrompt={pendingPrompt}
      onPromptSent={() => setPendingPrompt(null)}
      onClose={close}
    />
  );
};
